import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Table,
  Switch,
  Modal,
  Typography,
  Badge,
  Row,
  Col,
  Statistic,
  Radio,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  DollarOutlined,
  UserOutlined,
  TableOutlined,
  PlusOutlined,
  DownloadOutlined,
  GiftOutlined,
  CalendarOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import promoService from "../../services/promoService";
import masterService from "../../services/masterService";
import {
  noWhitespaceValidator,
  createExpirationDateValidator,
  createDisabledExpirationDate,
} from "../../utils/commonFunctions";
import { useCommon } from '../../context/CommonContext';  
const { Title, Text } = Typography;

const { Option } = Select;
const DATE_FORMAT = "DD-MM-YYYY";

// Prisma Decimal serializes as { s, e, d } over JSON. Convert to a plain number
// regardless of whether the backend already normalized it.
const toNumberOrNull = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const n = parseFloat(value);
    return isNaN(n) ? null : n;
  }
  if (typeof value === "object") {
    if (typeof value.toString === "function") {
      const n = parseFloat(value.toString());
      if (!isNaN(n)) return n;
    }
    if (Array.isArray(value.d) && typeof value.e === "number" && typeof value.s === "number") {
      let digitStr = "";
      value.d.forEach((part, i) => {
        digitStr += i === 0 ? String(part) : String(part).padStart(7, "0");
      });
      const exponent = value.e - (digitStr.length - 1);
      return value.s * parseFloat(digitStr) * Math.pow(10, exponent);
    }
  }
  return null;
};

const EMPTY_COUNTRY_IDS = [];

// A country newly added to the campaign always starts with a blank config —
// re-adding a previously removed country never resurrects its old settings.
// The promo type (discount vs amount) is campaign-wide (`promoType` state),
// so a config row only carries the per-country amount fields.
const blankCountryConfig = (country_id) => ({
  country_id,
  percent: undefined,
  fixed_amount: undefined,
  max_discount_amount: undefined,
  min_txn_amount: undefined,
});

const PromoCampaignForm = () => {
  const navigate = useNavigate();
  const { pc_id } = useParams();
  const [form] = Form.useForm();
  const [addPromosForm] = Form.useForm();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  // Active plans / plan types for the dropdowns; the campaign's plan/type is
  // inherited by every promo it spawns.
  const [plans, setPlans] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  // Bare-id placeholder shown when a saved plan/plan type was later deactivated.
  const [inactivePlanOption, setInactivePlanOption] = useState(null);
  const [inactivePlanTypeOption, setInactivePlanTypeOption] = useState(null);
  const [promosLoading, setPromosLoading] = useState(false);
  const [savedLimits, setSavedLimits] = useState({
    pc_per_user_limit: 0,
    pc_max_redeems: 0,
  });
  const [promos, setPromos] = useState([]);

  // Per-country discount/amount configuration, keyed in lockstep with the
  // "Applicable Countries" selection below.
  const [countryConfigs, setCountryConfigs] = useState([]);
  // Campaign-wide promo type ("discount_based" | "amount_based") — one choice
  // shared by every country; only the amount fields vary per country.
  const [promoType, setPromoType] = useState(undefined);
  const watchedCountryIds = Form.useWatch("country_ids", form) || EMPTY_COUNTRY_IDS;

  const syncCountryConfigsTo = (ids) => {
    setCountryConfigs((prev) => {
      const byId = new Map(prev.map((c) => [c.country_id, c]));
      return ids.map((id) => byId.get(id) || blankCountryConfig(id));
    });
  };

  useEffect(() => {
    syncCountryConfigsTo(watchedCountryIds);
  }, [watchedCountryIds]);

  const countryLabelById = useMemo(() => {
    const map = new Map();
    countries.forEach((c) => map.set(c.value, c.label));
    return map;
  }, [countries]);

  // Merge the deactivated placeholder (if any) into the active options.
  const planOptions = useMemo(
    () =>
      inactivePlanOption && !plans.some((p) => p.value === inactivePlanOption.value)
        ? [...plans, inactivePlanOption]
        : plans,
    [plans, inactivePlanOption]
  );
  const planTypeOptions = useMemo(
    () =>
      inactivePlanTypeOption && !planTypes.some((t) => t.value === inactivePlanTypeOption.value)
        ? [...planTypes, inactivePlanTypeOption]
        : planTypes,
    [planTypes, inactivePlanTypeOption]
  );

  const updateCountryConfig = (country_id, patch) => {
    setCountryConfigs((prev) =>
      prev.map((c) => (c.country_id === country_id ? { ...c, ...patch } : c))
    );
  };

  // Changing the campaign-wide type resets the type-specific amounts on every
  // country row (same behavior the old per-row type radio had).
  const onPromoTypeChange = (value) => {
    setPromoType(value);
    setCountryConfigs((prev) =>
      prev.map((c) => ({
        ...c,
        percent: undefined,
        fixed_amount: undefined,
        max_discount_amount: undefined,
      }))
    );
  };

  const [addPromosOpen, setAddPromosOpen] = useState(false);
  const [addPromosLoading, setAddPromosLoading] = useState(false);
  const [promoSearch, setPromoSearch] = useState("");

  const isEditMode = useMemo(() => {
    return pc_id && !isNaN(Number(pc_id));
  }, [pc_id]);

  const filteredPromos = useMemo(() => {
    const q = promoSearch.trim().toLowerCase();
    if (!q) return promos;
    return promos.filter((p) =>
      String(p.promo_code || "").toLowerCase().includes(q)
    );
  }, [promos, promoSearch]);

  useEffect(() => {
    setPageTitle(isEditMode ? "Edit Promo Campaign" : "Add Promo Campaign");
  }, [isEditMode, setPageTitle]);

  const fetchCountries = useCallback(async () => {
    try {
      const res = await masterService.getAllCountries({
        isAll: true,
        status: "Y",
      });
      const data = res?.data?.data?.data ?? res?.data?.data ?? [];
      const list = Array.isArray(data)
        ? data.map((c) => ({ label: c.country_name, value: c.country_id }))
        : [];
      setCountries(list);
    } catch (e) {
      console.error("Error loading countries", e);
      notify.error({ message: "Failed to load countries" });
    }
  }, [notify]);

  const fetchPlanOptions = useCallback(async () => {
    try {
      const res = await masterService.getAssignSubscriptionOptions({});
      const data = res?.data?.data ?? {};
      setPlans((data.subscription_plans || []).map((p) => ({ label: p.title, value: p.id })));
      setPlanTypes((data.plan_types || []).map((t) => ({ label: t.name, value: t.id })));
    } catch (e) {
      console.error("Error loading plans / plan types", e);
      notify.error({ message: "Failed to load plans / plan types" });
    }
  }, [notify]);

  const parseCampaignResponse = (res) => {
    const api = res?.data;
    const payload = api?.data !== undefined ? api.data : api;
    // Expecting { campaign, promos } or similar
    const campaign =
      payload?.campaign || payload?.data?.campaign || payload?.data || payload;
    const promosList = payload?.promos || payload?.data?.promos || [];
    return { campaign, promosList };
  };

  const fetchDetails = useCallback(async () => {
    if (!isEditMode) return;
    try {
      setPromosLoading(true);
      const res = await promoService.getCampaignsWithPromos(pc_id);
      const { campaign, promosList } = parseCampaignResponse(res);

      if (!campaign || typeof campaign !== "object") {
        notify.error({ message: "Campaign not found" });
        navigate("/promo-campaign");
        return;
      }

      setSavedLimits({
        pc_per_user_limit: campaign.pc_per_user_limit ?? 0,
        pc_max_redeems: campaign.pc_max_redeems ?? 0,
        target_audience: campaign.target_audience ?? "ALL_USER",
        catelog_enabled: campaign.catelog_enabled ?? false,
      });

      const countryIdsFromCampaign = Array.isArray(campaign.country_ids)
        ? campaign.country_ids
        : [];
      const configsList = Array.isArray(campaign.country_configs)
        ? campaign.country_configs
        : [];
      const configsByCountry = new Map(configsList.map((c) => [c.country_id, c]));
      // Promo type is campaign-wide — prefer the campaign-level flags, falling
      // back to the first country config for campaigns saved before that change.
      setPromoType(
        campaign.discount_based || configsList[0]?.discount_based
          ? "discount_based"
          : campaign.amount_based || configsList[0]?.amount_based
            ? "amount_based"
            : undefined
      );
      setCountryConfigs(
        countryIdsFromCampaign.map((id) => {
          const c = configsByCountry.get(id);
          if (!c) return blankCountryConfig(id);
          return {
            country_id: id,
            percent: toNumberOrNull(c.percent) ?? undefined,
            fixed_amount: toNumberOrNull(c.fixed_amount) ?? undefined,
            max_discount_amount: toNumberOrNull(c.max_discount_amount) ?? undefined,
            min_txn_amount: toNumberOrNull(c.min_txn_amount) ?? undefined,
          };
        })
      );

      form.setFieldsValue({
        pc_name: campaign.pc_name ?? undefined,
        pc_description: campaign.pc_description ?? undefined,
        pc_per_user_limit: campaign.pc_per_user_limit ?? 0,
        pc_max_redeems: campaign.pc_max_redeems ?? 0,
        target_audience: campaign.target_audience ?? "ALL_USER",
        catelog_enabled: campaign.catelog_enabled ?? false,
        country_ids: countryIdsFromCampaign,
        fk_plan_id: campaign.fk_plan_id ?? undefined,
        fk_plan_type_id: campaign.fk_plan_type_id ?? undefined,
        start: campaign.start
          ? dayjs(campaign.start, [DATE_FORMAT, "YYYY-MM-DD", "YYYY/MM/DD"])
          : undefined,
        expiration: campaign.expiration
          ? dayjs(campaign.expiration, [
              DATE_FORMAT,
              "YYYY-MM-DD",
              "YYYY/MM/DD",
            ])
          : undefined,
        is_active: campaign.is_active ?? "Y",
      });

      // If the saved plan / plan type was later deactivated (or deleted), it
      // won't be in the active dropdown — surface just its id so the admin can
      // tell it was deactivated.
      if (campaign.fk_plan_id != null && campaign.plan && (!campaign.plan.is_active || campaign.plan.is_deleted)) {
        setInactivePlanOption({ label: `${campaign.fk_plan_id} (inactive)`, value: campaign.fk_plan_id });
      }
      if (
        campaign.fk_plan_type_id != null &&
        campaign.plan_type &&
        (!campaign.plan_type.is_active || campaign.plan_type.is_deleted)
      ) {
        setInactivePlanTypeOption({ label: `${campaign.fk_plan_type_id} (inactive)`, value: campaign.fk_plan_type_id });
      }

      setPromos(Array.isArray(promosList) ? promosList : []);
    } catch (e) {
      console.error("Error loading campaign details", e);
      notify.error({ message: "Failed to load campaign details" });
    } finally {
      setPromosLoading(false);
    }
  }, [form, isEditMode, navigate, notify, pc_id]);

  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);
      await Promise.allSettled([fetchCountries(), fetchPlanOptions(), fetchDetails()]);
      setInitialLoading(false);
    };
    init();
  }, [fetchDetails, fetchCountries, fetchPlanOptions]);

  const onFinish = async (values) => {
    try {
      const pcName = (values.pc_name || "").trim();
      if (!pcName) {
        notify.error({ message: "Campaign name is required" });
        return;
      }

      const selectedCountryIds = Array.isArray(values.country_ids) ? values.country_ids : [];
      if (selectedCountryIds.length < 1) {
        notify.error({ message: "Please select at least one country" });
        return;
      }

      if (values.fk_plan_id == null) {
        notify.error({ message: "Please select a plan" });
        return;
      }
      if (values.fk_plan_type_id == null) {
        notify.error({ message: "Please select a plan type" });
        return;
      }

      if (!values.start || !values.expiration) {
        notify.error({ message: "Start and expiration dates are required" });
        return;
      }
      if (values.expiration.isBefore(values.start) || values.expiration.isSame(values.start)) {
        notify.error({ message: "Expiration date must be after start date" });
        return;
      }

      // The promo type is campaign-wide — validate it once, then validate each
      // country's amount fields against it.
      const discountBased = promoType === "discount_based";
      const amountBased = promoType === "amount_based";
      if (!discountBased && !amountBased) {
        notify.error({ message: "Please select a promo type" });
        return;
      }

      // --- Per-country amount validation gate (runs before the API call) ---
      const countryConfigPayload = [];
      for (const countryId of selectedCountryIds) {
        const cfg = countryConfigs.find((c) => c.country_id === countryId) || blankCountryConfig(countryId);
        const countryLabel = countryLabelById.get(countryId) || `Country ${countryId}`;

        if (discountBased) {
          const pct = Number(cfg.percent);
          if (cfg.percent == null || cfg.percent === "") {
            notify.error({ message: `Discount percentage is required for ${countryLabel}` });
            return;
          }
          if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
            notify.error({ message: `${countryLabel}: discount percentage must be between 1 and 100` });
            return;
          }
          const cap = Number(cfg.max_discount_amount);
          if (cfg.max_discount_amount != null && cfg.max_discount_amount !== "" && (isNaN(cap) || cap < 0)) {
            notify.error({ message: `${countryLabel}: max discount amount must be non-negative` });
            return;
          }
        }

        if (amountBased) {
          const fixed = Number(cfg.fixed_amount);
          if (cfg.fixed_amount == null || cfg.fixed_amount === "") {
            notify.error({ message: `Fixed amount is required for ${countryLabel}` });
            return;
          }
          if (!Number.isFinite(fixed) || fixed < 0) {
            notify.error({ message: `${countryLabel}: fixed amount must be non-negative` });
            return;
          }
        }

        const minTxn = Number(cfg.min_txn_amount);
        if (cfg.min_txn_amount != null && cfg.min_txn_amount !== "" && (isNaN(minTxn) || minTxn < 0)) {
          notify.error({ message: `${countryLabel}: min transaction amount must be non-negative` });
          return;
        }

        countryConfigPayload.push({
          country_id: countryId,
          discount_based: discountBased,
          amount_based: amountBased,
          percent: discountBased ? (cfg.percent ?? null) : null,
          fixed_amount: amountBased ? (cfg.fixed_amount ?? null) : null,
          max_discount_amount: discountBased ? (cfg.max_discount_amount ?? null) : null,
          min_txn_amount: cfg.min_txn_amount ?? null,
        });
      }
      // --- End of per-country validation ---

      const perUserLimit = Number(values.pc_per_user_limit);
      if (!Number.isFinite(perUserLimit) || perUserLimit <= 0) {
        notify.error({ message: "Per user limit must be greater than 0" });
        return;
      }
      if (isEditMode && perUserLimit < savedLimits.pc_per_user_limit) {
        notify.error({ message: `Per user limit can only be increased (current: ${savedLimits.pc_per_user_limit})` });
        return;
      }

      const maxRedeems = Number(values.pc_max_redeems);
      if (!Number.isFinite(maxRedeems) || maxRedeems <= 0) {
        notify.error({ message: "Max redeems must be greater than 0" });
        return;
      }
      if (isEditMode && maxRedeems < savedLimits.pc_max_redeems) {
        notify.error({ message: `Max redeems can only be increased (current: ${savedLimits.pc_max_redeems})` });
        return;
      }
      // --- End of frontend validation ---

      setLoading(true);
      const payload = {
        pc_name: pcName,
        pc_description: values.pc_description,
        pc_id: isEditMode ? Number(pc_id) : values.pc_id,
        pc_per_user_limit: values.pc_per_user_limit ?? 0,
        pc_max_redeems: values.pc_max_redeems ?? 0,
        target_audience: values.target_audience ?? "ALL_USER",
        catelog_enabled: values.catelog_enabled ?? false,
        fk_plan_id: values.fk_plan_id,
        fk_plan_type_id: values.fk_plan_type_id,
        discount_based: discountBased,
        amount_based: amountBased,
        country_configs: countryConfigPayload,
        start: values.start ? values.start.format(DATE_FORMAT) : null,
        expiration: values.expiration
          ? values.expiration.format(DATE_FORMAT)
          : null,
        is_active: values.is_active,
      };
      const res = await promoService.addEditCampaigns(payload);
      if (res?.data?.status) {
        notify.success({ message: res?.data?.message || "Success" });
        if (!isEditMode) {
          const savedId = res?.data?.data?.pc_id || res?.data?.pc_id;
          if (savedId) {
            navigate("/promo-campaign");
            return;
          }
          form.resetFields();
        }
        navigate("/promo-campaign");
      } else {
        notify.error({ message: res?.data?.message || "Not Saved" });
      }
    } catch (e) {
      console.error("Error saving campaign", e);
      notify.error({ message: e?.data?.message || "Failed to save campaign" });
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    form.resetFields();
    setPromoType(undefined);
  };
  const onGoBack = () => navigate("/promo-campaign");

  // Create date validators using common functions
  const validateExpirationDate = createExpirationDateValidator(form);
  const disabledExpirationDate = createDisabledExpirationDate(form);

  const handleTogglePromoStatus = async (checked, record) => {
    try {
      const status = checked ? "Y" : "N";
      await promoService.setCampaignPromoStatus(
        Number(pc_id),
        record.promo_id,
        status
      );
      notify.success({ message: checked ? "Activated" : "Deactivated" });
      // Optimistic update
      setPromos((prev) =>
        prev.map((p) =>
          p.promo_id === record.promo_id ? { ...p, is_active: status } : p
        )
      );
    } catch (e) {
      console.error("Error changing promo status", e);
      notify.error({ message: "Failed to change status" });
      fetchDetails();
    }
  };

  // Limits that mirror the backend constants so validation stays in sync.
  const MAX_CODES_PER_REQUEST = 100;
  const MAX_CODES_PER_CAMPAIGN = 500;
  // Every code (prefix + random suffix) is exactly this long.
  const PROMO_CODE_LENGTH = 10;
  const SPAWN_PREFIX_MIN_LENGTH = 2;
  // prefix can be at most (code_length - min_random_suffix) = 10 - 2 = 8 chars
  const SPAWN_PREFIX_MAX_LENGTH = 8;

  const handleAddPromos = async () => {
    if (addPromosLoading) return; // prevent double-submit
    if (!isEditMode) {
      notify.error({ message: "Save Campaign First" });
      setAddPromosOpen(false);
      return;
    }
    try {
      const values = await addPromosForm.validateFields();
      setAddPromosLoading(true);
      setPromosLoading(true);
      const res = await promoService.addPromosForCampaigns(
        Number(pc_id),
        Number(values.count),
        { prefix: values.prefix }
      );
      const apiData = res?.data;
      if (apiData?.status) {
        notify.success({ message: apiData.message || "Promo codes generated successfully" });
        setAddPromosOpen(false);
        addPromosForm.resetFields();
        await fetchDetails();
      } else {
        notify.error({ message: apiData?.message || "Failed to generate promo codes" });
      }
    } catch (e) {
      if (e?.errorFields) {
        // Form validation failure — antd already shows inline messages.
        return;
      }
      const msg = e?.response?.data?.message || e?.data?.message || "Server error while generating promo codes";
      console.error("Error adding promos", e);
      notify.error({ message: msg });
    } finally {
      setAddPromosLoading(false);
      setPromosLoading(false);
    }
  };

  const downloadCsv = () => {
    const headers = ["Promo Code", "Redeems", "Status"];
    const rows = promos.map((p) => [
      p.promo_code,
      p.no_of_redeem_used,
      p.is_active,
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((v) =>
            v === null || v === undefined ? "" : String(v).replace(/"/g, '""')
          )
          .map((v) => (v.includes(",") || v.includes('"') ? `"${v}"` : v))
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${
        form.getFieldValue("pc_name") || "campaign-promos"
      }-campaign-promos.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      render: (_t, _r, index) => index + 1,
    },
    {
      title: "Promo Code",
      dataIndex: "promo_code",
      sorter: (a, b) => (a.promo_code || "").localeCompare(b.promo_code || ""),
      render: (text) => (
        <Text code className="font-mono text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: "Redeems",
      dataIndex: "no_of_redeem_used",
      sorter: (a, b) => (a.no_of_redeem_used || 0) - (b.no_of_redeem_used || 0),
      render: (text) => (
        <Badge
          count={text || 0}
          style={{ backgroundColor: text > 0 ? "#52c41a" : "#d9d9d9" }}
        />
      ),
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      fixed: "right",
      width: 100,
      align: "center",
      sorter: (a, b) => (a.is_active === "Y" ? 1 : 0) - (b.is_active === "Y" ? 1 : 0),
      render: (text, record) => (
        <Switch
          checked={text === "Y"}
          onChange={(checked) => handleTogglePromoStatus(checked, record)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen ">
      <div className="">
        {/* Header Section */}

        <Card
          loading={initialLoading}
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={onFinish}
            initialValues={{
              is_active: "Y",
              country_ids: [],
              pc_per_user_limit: 1,
              pc_max_redeems: 1,
            }}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <GiftOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Campaign Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Campaign Name
                      </span>
                    }
                    name="pc_name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a campaign name",
                      },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter campaign name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                    extra="Campaign status is independent — deactivating the campaign does not deactivate its spawned promo codes. Toggle individual promos below."
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Description
                      </span>
                    }
                    name="pc_description"
                    rules={[{ validator: noWhitespaceValidator }]}
                  >
                    <Input.TextArea
                      maxLength={255}
                      rows={3}
                      placeholder="Enter campaign description"
                      className="rounded-lg"
                      showCount
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Usage Limits Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <UserOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Usage Limits
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Per User Limit
                      </span>
                    }
                    name="pc_per_user_limit"
                    extra={
                      isEditMode
                        ? `Can only be increased (current: ${savedLimits.pc_per_user_limit})`
                        : undefined
                    }
                    rules={[
                      { required: true, message: "Please enter per user limit" },
                      {
                        validator: (_, value) => {
                          if (value == null || value === "") return Promise.resolve();
                          if (Number(value) <= 0) {
                            return Promise.reject(
                              new Error("Per user limit must be greater than 0")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!isEditMode) return Promise.resolve();
                          const next = value ?? 0;
                          if (next < savedLimits.pc_per_user_limit) {
                            return Promise.reject(
                              new Error(
                                `Per user limit can only be increased (current: ${savedLimits.pc_per_user_limit})`
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={isEditMode ? Math.max(1, savedLimits.pc_per_user_limit) : 1}
                      placeholder="Enter per user limit"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Max Total Redeems
                      </span>
                    }
                    name="pc_max_redeems"
                    extra={
                      isEditMode
                        ? `Can only be increased (current: ${savedLimits.pc_max_redeems})`
                        : undefined
                    }
                    rules={[
                      { required: true, message: "Please enter max redeems" },
                      {
                        validator: (_, value) => {
                          if (value == null || value === "") return Promise.resolve();
                          if (Number(value) <= 0) {
                            return Promise.reject(
                              new Error("Max redeems must be greater than 0")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!isEditMode) return Promise.resolve();
                          const next = value ?? 0;
                          if (next < savedLimits.pc_max_redeems) {
                            return Promise.reject(
                              new Error(
                                `Max redeems can only be increased (current: ${savedLimits.pc_max_redeems})`
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={isEditMode ? Math.max(1, savedLimits.pc_max_redeems) : 1}
                      placeholder="Enter max redeems"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span className="font-semibold">Target Audience</span>
                    }
                    name="target_audience"
                    initialValue="ALL_USER"
                    extra="Applies to every code generated by this campaign."
                    rules={[
                      {
                        required: true,
                        message: "Please select target audience",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select target audience"
                      className="rounded-lg"
                    >
                      <Option value="ALL_USER">All users</Option>
                      <Option value="FIRST_TIME_USER">
                        First-time users only
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span className="font-semibold">Show In Catalogue</span>
                    }
                    name="catelog_enabled"
                    initialValue={false}
                    extra="Badges this campaign's plan and plan type with 'Offer Available' on the subscription page, for the target audience above. Display only — it does not change who may redeem the codes."
                  >
                    <Select placeholder="Select" className="rounded-lg">
                      <Option value={false}>No</Option>
                      <Option value={true}>Yes</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Plans & Validity Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <CalendarOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Plans & Validity
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={24} lg={12}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Applicable Countries
                      </span>
                    }
                    name="country_ids"
                    extra="Applies to all promo codes spawned by this campaign."
                    rules={[
                      {
                        required: true,
                        type: "array",
                        min: 1,
                        message: "Please select at least one country",
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select countries"
                      showSearch
                      allowClear
                      className="rounded-lg"
                      size="large"
                      options={countries}
                      optionFilterProp="label"
                      onChange={(ids) => syncCountryConfigsTo(ids || [])}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    label={
                      <span className="font-semibold" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                        Plan
                      </span>
                    }
                    name="fk_plan_id"
                    extra="Applies to all promo codes spawned by this campaign."
                    rules={[{ required: true, message: "Please select a plan" }]}
                  >
                    <Select
                      placeholder="Select plan"
                      showSearch
                      allowClear
                      className="rounded-lg"
                      size="large"
                      options={planOptions}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Form.Item
                    label={
                      <span className="font-semibold" style={{ color: "rgba(0, 0, 0, 0.88)" }}>
                        Plan Type
                      </span>
                    }
                    name="fk_plan_type_id"
                    rules={[{ required: true, message: "Please select a plan type" }]}
                  >
                    <Select
                      placeholder="Select plan type"
                      showSearch
                      allowClear
                      className="rounded-lg"
                      size="large"
                      options={planTypeOptions}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>

              </Row>

              {/* Discount configuration — the promo type is chosen ONCE for the
                  whole campaign; each country selected above then gets its own
                  amount fields for that type. */}
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <div className="flex items-center gap-2 mb-2">
                    <PercentageOutlined />
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Discount Configuration
                    </span>
                  </div>

                  <div className="mb-3">
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Promo Type
                    </span>
                    <div className="mt-1">
                      <Radio.Group
                        value={promoType}
                        onChange={(e) => onPromoTypeChange(e.target.value)}
                      >
                        <Radio value="discount_based">Discount (%)</Radio>
                        <Radio value="amount_based">Amount</Radio>
                      </Radio.Group>
                    </div>
                    <Text type="secondary">
                      Applies to all countries — only the amounts below vary per country.
                    </Text>
                  </div>

                  {countryConfigs.length === 0 ? (
                    <Text type="secondary">
                      Select countries above to configure discount/amount settings per country.
                    </Text>
                  ) : (
                    <Table
                      dataSource={countryConfigs}
                      rowKey="country_id"
                      pagination={false}
                      scroll={{ x: 900 }}
                      className="mb-4"
                      columns={[
                        {
                          title: "Country",
                          dataIndex: "country_id",
                          width: 160,
                          fixed: "left",
                          render: (id) => countryLabelById.get(id) || `Country ${id}`,
                        },
                        {
                          title: "Percent",
                          dataIndex: "percent",
                          width: 130,
                          render: (percent, row) =>
                            promoType === "discount_based" ? (
                              <InputNumber
                                style={{ width: "100%" }}
                                min={1}
                                max={100}
                                suffix="%"
                                placeholder="%"
                                value={percent}
                                onChange={(v) => updateCountryConfig(row.country_id, { percent: v })}
                              />
                            ) : (
                              <Text type="secondary">—</Text>
                            ),
                        },
                        {
                          title: "Max Discount",
                          dataIndex: "max_discount_amount",
                          width: 150,
                          render: (val, row) =>
                            promoType === "discount_based" ? (
                              <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                precision={2}
                                step={0.01}
                                placeholder="Optional"
                                prefix={<DollarOutlined />}
                                value={val}
                                onChange={(v) => updateCountryConfig(row.country_id, { max_discount_amount: v })}
                              />
                            ) : (
                              <Text type="secondary">—</Text>
                            ),
                        },
                        {
                          title: "Fixed Amount",
                          dataIndex: "fixed_amount",
                          width: 150,
                          render: (val, row) =>
                            promoType === "amount_based" ? (
                              <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                precision={2}
                                step={0.01}
                                placeholder="Amount"
                                prefix={<DollarOutlined />}
                                value={val}
                                onChange={(v) => updateCountryConfig(row.country_id, { fixed_amount: v })}
                              />
                            ) : (
                              <Text type="secondary">—</Text>
                            ),
                        },
                        {
                          title: "Min Txn Amount",
                          dataIndex: "min_txn_amount",
                          width: 160,
                          render: (val, row) => (
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              precision={2}
                              step={0.01}
                              placeholder="Optional"
                              prefix={<DollarOutlined />}
                              value={val}
                              onChange={(v) => updateCountryConfig(row.country_id, { min_txn_amount: v })}
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Start Date
                      </span>
                    }
                    name="start"
                    rules={[
                      { required: true, message: "Please select a start date" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format={DATE_FORMAT}
                      placeholder="Select start date"
                      className="rounded-lg"
                      size="large"
                      onChange={() => {
                        // Re-validate expiration date when start date changes
                        form.validateFields(["expiration"]);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Expiration Date
                      </span>
                    }
                    name="expiration"
                    rules={[
                      {
                        required: true,
                        message: "Please select an expiration date",
                      },
                      { validator: validateExpirationDate },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format={DATE_FORMAT}
                      placeholder="Select expiration date"
                      className="rounded-lg"
                      size="large"
                      disabledDate={disabledExpirationDate}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex flex-wrap justify-end gap-3 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space wrap size="middle">
                <Button
                  onClick={onReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={onGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Saving..." : "Save Campaign"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>

        {/* Promo Codes Management Section */}
        {isEditMode && (
          <Card
            className="shadow-lg border-0"
            styles={{
              body: {
                padding: "32px",
                backgroundColor: "#ffffff",
                borderColor: "#d9d9d9",
              },
            }}
          >
            <div
              className="bg-white p-6 rounded-lg border border-gray-200 mb-6"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2">
                  <TableOutlined />
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Promo Codes Management
                  </Title>
                  <Badge
                    count={promos.length}
                  />
                </div>
                <Space wrap>
                  <Input.Search
                    allowClear
                    placeholder="Search promo code"
                    value={promoSearch}
                    onChange={(e) => setPromoSearch(e.target.value)}
                    style={{ flex: '1 1 160px', maxWidth: 240 }}
                  />
                  <Button
                    onClick={downloadCsv}
                    type="primary"
                    icon={<DownloadOutlined />}
                  >
                    Download CSV
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setAddPromosOpen(true)}
                    icon={<PlusOutlined />}
                  >
                    Add Promo Codes
                  </Button>
                </Space>
              </div>

              {/* Statistics Row */}
              <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={8}>
                  <Card size="small" className="text-center">
                    <Statistic
                      title="Total Codes"
                      value={promos.length}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" className="text-center">
                    <Statistic
                      title="Active Codes"
                      value={promos.filter((p) => p.is_active === "Y").length}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card size="small" className="text-center">
                    <Statistic
                      title="Total Redeems"
                      value={promos.reduce(
                        (sum, p) =>
                          sum + (p.no_of_redeem_used || 0),
                        0
                      )}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

            <Table
              loading={promosLoading}
              dataSource={filteredPromos}
              columns={columns}
              rowKey={(record) => record.promo_id}
              scroll={{ x: 500 }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} promo codes`,
              }}
              className="rounded-lg"
            />
          </Card>
        )}

        {/* Add Promos Modal */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <span>Add Promo Codes to Campaign</span>
            </div>
          }
          open={addPromosOpen}
          onCancel={() => setAddPromosOpen(false)}
          width={600}
          style={{ maxWidth: '95vw' }}
          footer={null}
        >
          <Card className="border-0 shadow-none">
            <Form
              form={addPromosForm}
              layout="vertical"
              size="large"
              initialValues={{ count: undefined, prefix: "" }}
            >
              <Form.Item
                label={
                  <span
                    className="font-semibold"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    How many promo codes to generate?
                  </span>
                }
                name="count"
                extra={`Max ${MAX_CODES_PER_REQUEST} codes per request · Campaign total limit: ${MAX_CODES_PER_CAMPAIGN} codes`}
                rules={[
                  { required: true, message: "Please enter a promo count" },
                  {
                    validator: (_, value) => {
                      if (value == null || value === "") return Promise.resolve();
                      const n = Number(value);
                      if (!Number.isInteger(n)) {
                        return Promise.reject(new Error("Count must be a whole number"));
                      }
                      if (n < 1) {
                        return Promise.reject(new Error("Count cannot be less than 1"));
                      }
                      if (n > MAX_CODES_PER_REQUEST) {
                        return Promise.reject(
                          new Error(`Count cannot exceed ${MAX_CODES_PER_REQUEST} per request (campaign limit: ${MAX_CODES_PER_CAMPAIGN} total)`)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  max={MAX_CODES_PER_REQUEST}
                  precision={0}
                  placeholder={`Enter number of codes (1 – ${MAX_CODES_PER_REQUEST})`}
                  className="rounded-lg"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span
                    className="font-semibold"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Promo Code Prefix
                  </span>
                }
                name="prefix"
                normalize={(v) => (v || "").toUpperCase()}
                extra={`Min ${SPAWN_PREFIX_MIN_LENGTH} chars · Max ${SPAWN_PREFIX_MAX_LENGTH} chars · Uppercase only · No spaces · Every code is exactly ${PROMO_CODE_LENGTH} characters (prefix + random suffix)`}
                rules={[
                  { required: true, message: "Please enter a promo prefix" },
                  { validator: noWhitespaceValidator },
                  {
                    validator: (_, value) => {
                      if (value == null || value === "") return Promise.resolve();
                      const v = String(value);
                      if (/\s/.test(v)) {
                        return Promise.reject(
                          new Error("Promo code prefix must be a single word (no spaces)")
                        );
                      }
                      if (v.length < SPAWN_PREFIX_MIN_LENGTH) {
                        return Promise.reject(
                          new Error(`Prefix must be at least ${SPAWN_PREFIX_MIN_LENGTH} characters`)
                        );
                      }
                      if (v.length > SPAWN_PREFIX_MAX_LENGTH) {
                        return Promise.reject(
                          new Error(
                            `Prefix cannot exceed ${SPAWN_PREFIX_MAX_LENGTH} characters (code needs room for random suffix)`
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Enter prefix (e.g., SUMMER, SALE)"
                  className="rounded-lg"
                  style={{ textTransform: "uppercase" }}
                />
              </Form.Item>

              <div className="flex justify-end gap-3 pt-4">
                <Button onClick={() => setAddPromosOpen(false)} size="large" disabled={addPromosLoading}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleAddPromos}
                  icon={<PlusOutlined />}
                  size="large"
                  className="px-6"
                  loading={addPromosLoading}
                  disabled={addPromosLoading}
                >
                  Generate Codes
                </Button>
              </div>
            </Form>
          </Card>
        </Modal>
      </div>
    </div>
  );
};

export default PromoCampaignForm;
